import { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useDroppable,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { Star } from 'lucide-react';
import type { UserFavoriteWithArche, WorktopLayout } from '@/lib/api';
import { useIsDesktop } from '../hooks/useIsDesktop';
import { FavoriteCard, FavoriteIcon } from './FavoriteCard';

/** Column counts per layout. Each layout keeps its own independent position memory. */
const MOBILE_COLS = 5;
const DESKTOP_COLS = 7;
/** Minimum rows always visible */
const MIN_ROWS = 7;
/** Extra empty rows below the last occupied cell */
const EXTRA_ROWS = 2;

interface FavoritesGridProps {
  favorites: UserFavoriteWithArche[];
  onReorder: (placements: { archId: string; position: number }[], layout: WorktopLayout) => void;
}

/**
 * Pure placement math for a drag, isolated from the DnD library so the persistence
 * contract is unit-testable. `posOf` selects the active layout's position field, so every
 * returned position — and therefore everything persisted — is scoped to that one layout;
 * the other layout's column is never touched. Returns null for a no-op drag.
 */
export function computeDragPlacements(
  favorites: UserFavoriteWithArche[],
  draggedArchId: string,
  overId: string,
  posOf: (f: UserFavoriteWithArche) => number,
): { archId: string; position: number }[] | null {
  const sortedFavorites = [...favorites].sort((a, b) => posOf(a) - posOf(b));
  const sortableIds = sortedFavorites.map((f) => f.archId);

  if (overId.startsWith('empty_')) {
    // --- Placement on empty cell --- dragged item takes the target cell; others stay put.
    const targetPosition = parseInt(overId.replace('empty_', ''), 10);
    return favorites.map((f) => ({
      archId: f.archId,
      position: f.archId === draggedArchId ? targetPosition : posOf(f),
    }));
  }

  // --- Shift among sortable items --- items take the positions of the slots they now occupy.
  const oldIndex = sortableIds.indexOf(draggedArchId);
  const newIndex = sortableIds.indexOf(overId);
  if (oldIndex === -1 || newIndex === -1) return null;

  const newOrder = arrayMove(sortableIds, oldIndex, newIndex);
  const sortedPositions = sortedFavorites.map(posOf);
  return newOrder.map((archId, i) => ({ archId, position: sortedPositions[i] }));
}

export function FavoritesGrid({ favorites, onReorder }: FavoritesGridProps) {
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const isDesktop = useIsDesktop();

  // Active layout drives both the column count and which position field we read/write.
  const cols = isDesktop ? DESKTOP_COLS : MOBILE_COLS;
  const layout: WorktopLayout = isDesktop ? 'desktop' : 'mobile';
  const posOf = (f: UserFavoriteWithArche) => (isDesktop ? f.desktopPosition : f.mobilePosition);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  // Sorted for consistent ordering
  const sortedFavorites = [...favorites].sort((a, b) => posOf(a) - posOf(b));
  const sortableIds = sortedFavorites.map((f) => f.archId);

  // Position → favorite lookup (within the active layout)
  const positionMap = new Map<number, UserFavoriteWithArche>();
  for (const fav of favorites) {
    positionMap.set(posOf(fav), fav);
  }

  // Grid size
  const maxPosition = favorites.length > 0 ? Math.max(...favorites.map(posOf)) : -1;
  const minCells = MIN_ROWS * cols;
  const cellsNeeded = maxPosition + 1 + EXTRA_ROWS * cols;
  const cellCount = Math.ceil(Math.max(minCells, cellsNeeded) / cols) * cols;

  const activeFavorite = activeDragId ? favorites.find((f) => f.archId === activeDragId) : null;

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // `posOf`/`layout` are both derived from the active breakpoint, so the placements and
    // the persisted layout always agree.
    const placements = computeDragPlacements(
      favorites,
      active.id as string,
      String(over.id),
      posOf,
    );
    if (placements) onReorder(placements, layout);
  };

  if (favorites.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Star className="h-12 w-12 text-muted-foreground/30 mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">Your worktop is empty</h3>
        <p className="text-muted-foreground max-w-md">
          Click the star icon on any arche to add it to your worktop for quick access.
        </p>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={sortableIds} strategy={rectSortingStrategy}>
        <div className="grid grid-cols-5 lg:grid-cols-7 gap-x-3 sm:gap-x-6 gap-y-4 max-w-xl lg:max-w-3xl mx-auto">
          {Array.from({ length: cellCount }, (_, cellIndex) => {
            const fav = positionMap.get(cellIndex);
            if (fav) {
              return <FavoriteCard key={fav.archId} favorite={fav} />;
            }
            return (
              <EmptyCell
                key={`empty_${cellIndex}`}
                id={`empty_${cellIndex}`}
                isDragActive={activeDragId !== null}
              />
            );
          })}
        </div>
      </SortableContext>

      <DragOverlay dropAnimation={null}>
        {activeFavorite ? (
          <div className="w-20 flex flex-col items-center">
            <FavoriteIcon favorite={activeFavorite} />
            <span className="mt-2 text-xs font-medium text-foreground text-center line-clamp-2 w-full px-1">
              {activeFavorite.archeName}
            </span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

// =============================================================================
// Empty grid cell — standalone droppable (not sortable)
// =============================================================================

function EmptyCell({ id, isDragActive }: { id: string; isDragActive: boolean }) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={`min-h-[5rem] rounded-2xl transition-colors ${
        isDragActive ? 'border-2 border-dashed border-border/30' : ''
      } ${isOver ? 'bg-primary/10 border-primary/40!' : ''}`}
    />
  );
}
